import "./tests.page.css"
import useAnimateIn from "../../hooks/useAnimateIn"
import { useEffect, useState } from "react"
import API from "../../api"
import SkeletonComponent from "../../components/skeleton/skeleton.component"
import moment from "moment"
import { Link, useNavigate } from "react-router-dom"
import { orderCheckIcon, plusIcon } from "../../icons"
import ModalComponent from "../../components/modal/modal.component"
import DropdownComponent from "../../components/dropdown/dropdown.component"
import { ToastContainer, toast } from "react-toastify"
import QRCode from "react-qr-code"

const TestsPage = (props) => {
  const { user, userMeta } = props
  const [loading, setLoading] = useState(true)
  const [tests, setTests] = useState([])
  const [filteredTests, setFilteredTests] = useState([])
  const [search, setSearch] = useState("")
  const [showNewTest, setShowNewTest] = useState()
  const [trials, setTrials] = useState([])
  const [patients, setPatients] = useState([])
  const [newTest, setNewTest] = useState({
    patient: "",
    trial: "",
    dateCollected: "",
    notes: "",
  })
  const [testSubmitted, setTestSubmitted] = useState()
  const navigate = useNavigate()

  // Fetch on load
  useEffect(() => {
    const fetchTests = async () => {
      try {
        const testsRes = await API.makeGetRequest(user, "tests")
        const patientsRes = await API.makeGetRequest(user, "patients")
        const trialsRes = await API.makeGetRequest(user, "trials")
        setTrials(
          trialsRes.map((t) => {
            return {
              key: t.id,
              label: t.name,
            }
          })
        )

        setPatients(
          patientsRes.map((t) => {
            return {
              key: t.id,
              label: t.name,
            }
          })
        )

        setTests(testsRes)
        setFilteredTests(testsRes)
      } catch {
        // todo handle error
      } finally {
        setLoading(false)
      }
    }

    if (user) {
      fetchTests()
    }
  }, [user])

  const handleSearch = (val) => {
    setSearch(val)

    // Search
    if (val.length > 0) {
      setFilteredTests(
        [...tests].filter((t) => {
          const v = val.toLowerCase()
          return (
            t.id.toLowerCase().indexOf(v) > -1 ||
            t.patientId.toLowerCase().indexOf(v) > -1 ||
            t.trialId.toLowerCase().indexOf(v) > -1
          )
        })
      )
    } else {
      setFilteredTests([...tests])
    }
  }

  const handleStatusClick = (t) => {
    if (t.status === "Ready") {
      navigate(`/tests/${t.id}`)
    }
  }

  const getModalContent = () => {
    return !testSubmitted ? (
      <div className="cb-modal-form">
        <p>Please fill in the following fields</p>
        <h6>
          Patient ID<sup>*</sup>
        </h6>
        <DropdownComponent
          placeholder="Select a patient"
          options={patients}
          value={newTest.patient}
          onChange={(o) => setNewTest({ ...newTest, patient: o })}
        />
        <h6>
          Clinical Trials<sup>*</sup>
        </h6>
        <DropdownComponent
          placeholder="Select a trial"
          options={trials}
          value={newTest.trial}
          onChange={(o) => setNewTest({ ...newTest, trial: o })}
        />
        <h6>
          Date Sample Collected<sup>*</sup>
        </h6>
        <input
          type="date"
          value={newTest.dateCollected}
          placeholder="MM/DD/YYYY"
          onChange={(e) =>
            setNewTest({ ...newTest, dateCollected: e.target.value })
          }
        />
        <h6>Notes (optional)</h6>
        <textarea
          value={newTest.notes}
          placeholder="Add some notes..."
          onChange={(e) => setNewTest({ ...newTest, notes: e.target.value })}
        />
      </div>
    ) : (
      <div className="cb-test-submitted">
        {orderCheckIcon}
        <h5>Order Successfully Sent!</h5>
        <h6>
          Test ID: <span>{newTest.id}</span>
        </h6>
        <h6>
          Patient ID: <span>{newTest.patient.key}</span>
        </h6>
        <h6>
          Trial ID: <span>{newTest.trial.key}</span>
        </h6>
        <h6>
          Date: <span>{moment().format("MM/DD/YYYY")}</span>
        </h6>
        <h6>
          Notes: <span>{newTest.notes || "None..."}</span>
        </h6>
        <div className="cb-qr-container">
          <div className="cb-qr">
            <QRCode
              fgColor="#2F0B5B"
              value={`https://scout.cellsbin.com/tests/${newTest.id}`}
            />
          </div>
          <div className="cb-qr-info">
            <h6>
              <span>Patient ID:{newTest.patient.key}</span>
            </h6>
            <h6>
              <span>Trial ID: {newTest.trial.key}</span>
            </h6>
            <h6>
              <span>Date: {moment().format("MM/DD/YYYY")}</span>
            </h6>
          </div>
        </div>
      </div>
    )
  }

  const closeNewTest = () => {
    setShowNewTest(false)
    setNewTest({ patient: "", trial: "", dateCollected: "", notes: "" })
  }

  const getModalActions = () => {
    return !testSubmitted ? (
      <>
        <button
          className="cb-primary-btn"
          onClick={handleNewTest}
          disabled={
            !newTest.patient || !newTest.trial || !newTest.dateCollected
          }
        >
          Order
        </button>
        <button className="cb-text-btn" onClick={closeNewTest}>
          Cancel
        </button>
      </>
    ) : (
      <>
        <button className="cb-primary-btn" onClick={closeNewTest}>
          Print Label
        </button>
        <button className="cb-text-btn" onClick={closeNewTest}>
          Continue
        </button>
      </>
    )
  }

  const handleNewTest = async () => {
    try {
      setTestSubmitted(false)
      setLoading(true)
      const newTestObj = { ...newTest }
      delete newTestObj.trial
      delete newTestObj.patient
      newTestObj.trialId = newTest.trial.key
      newTestObj.patientId = newTest.patient.key
      newTestObj.status = "Ordered"
      const newTestId = await API.makePostRequest(user, "tests", newTestObj)
      setNewTest({ ...newTest, id: newTestId })
      setTests([{ ...newTestObj, id: newTestId }, ...tests])
      setFilteredTests([{ ...newTestObj, id: newTestId }, ...filteredTests])
      setTestSubmitted(true)
    } catch (e) {
      toast.error("Uh oh. Failed to create a test -- please try again")
      // Do something here
    } finally {
      setLoading(false)
    }
  }

  useAnimateIn()

  return (
    <div className="cb-page cb-tests">
      <ToastContainer position="top-center" theme="dark" />

      {/* New test */}
      {showNewTest && (
        <ModalComponent
          title={testSubmitted ? "" : "Order New Test"}
          content={getModalContent()}
          onClose={() => setShowNewTest(false)}
          actions={getModalActions()}
        />
      )}

      <div className="cb-page-header">
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>
      <div className="cb-page-content">
        <h2>Hello {userMeta.fullName}</h2>
        <h5>Here's all of your tests</h5>

        <div className="cb-list-view">
          <div className="cb-list-view-header">
            <div>
              <h4>Tests</h4>
              <h6>
                Last Updated at{" "}
                {moment().subtract(1, "hour").format("MM/DD/YYYY hh:mm a")}
              </h6>
            </div>
            <button
              className="order-new-btn"
              onClick={() => setShowNewTest(true)}
              disabled={showNewTest}
            >
              {plusIcon} Order New Test
            </button>
          </div>
          {loading && <SkeletonComponent />}
          <ul className="cb-list">
            <li className="cb-list-row row-header">
              <div>Test ID</div>
              <div>Patient ID</div>
              <div>Trial</div>
              <div>Date Ordered</div>
              <div>Status</div>
              <div>Last update</div>
            </li>
            {filteredTests?.map((t) => {
              return (
                <li className="cb-list-row" key={t.id}>
                  <div>
                    <Link to={`/tests/${t.id}`}>{t.id}</Link>
                  </div>
                  <div>{t.patientId}</div>
                  <div>{t.trialId}</div>
                  <div>{moment(t.orderDate).format("MM/DD/YYYY")}</div>
                  <div>
                    <span
                      onClick={() => handleStatusClick(t)}
                      className={`cb-status-tag ${t.status
                        .replace(" ", "-")
                        .toLowerCase()}`}
                    >
                      {t.status}
                    </span>
                  </div>
                  <div>
                    {t.resultDate
                      ? moment(t.resultDate).format("MM/DD/YYYY")
                      : moment(t.orderDate).format("MM/DD/YYYY")}
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    </div>
  )
}

export default TestsPage
